import React, { useEffect, useRef } from 'react'
import {
    StyleSheet,
    View,
    Modal,
    Animated,
    KeyboardAvoidingView,
    Platform,
    SafeAreaView,
    Text,
    TouchableWithoutFeedback,
    useWindowDimensions,
    TouchableOpacity
} from 'react-native'
import { Entypo } from '@expo/vector-icons'
import RenderHtml, {
    HTMLElementModel,
    HTMLContentModel
} from 'react-native-render-html'
import { useTranslation } from 'react-i18next'

interface ModalDrawerProps {
    visible: boolean
    headerText: string
    bodyText: string
    position?: 'center' | 'top' | 'bottom'
    screen?: 'full' | 'dropdown'
    flatTop?: boolean
    flatBottom?: boolean
    offCanvas?: boolean
    onClose: () => void // Function to close the modal
}

const ModalDrawer = ({
    visible,
    headerText,
    bodyText,
    position = 'center',
    screen,
    flatTop,
    flatBottom,
    offCanvas = false,
    onClose
}: ModalDrawerProps) => {
    const { width: DEVICE_WIDTH } = useWindowDimensions()

    const scaleValue = useRef(new Animated.Value(0)).current
    const translateValue = useRef(new Animated.Value(DEVICE_WIDTH)).current

    const { t } = useTranslation()

    useEffect(() => {
        toggleModal()
    }, [visible])

    const toggleModal = () => {
        if (visible) {
            if (offCanvas) {
                Animated.timing(translateValue, {
                    toValue: 0,
                    duration: 300,
                    useNativeDriver: true
                }).start()
            } else {
                Animated.spring(scaleValue, {
                    toValue: 1,
                    useNativeDriver: false
                }).start()
            }
        } else {
            if (offCanvas) {
                Animated.timing(translateValue, {
                    toValue: DEVICE_WIDTH,
                    duration: 300,
                    useNativeDriver: true
                }).start()
            } else {
                Animated.timing(scaleValue, {
                    toValue: 0,
                    duration: 300,
                    useNativeDriver: false
                }).start()
            }
        }
    }

    return (
        <Modal transparent visible={visible}>
            <SafeAreaView style={{ flex: 1 }}>
                <KeyboardAvoidingView
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                    style={{ flex: 1 }}
                >
                    <TouchableWithoutFeedback onPress={onClose}>
                        <View
                            style={[
                                styles.modalBackground,
                                position === 'top' && styles.modalBackgroundTop,
                                position === 'bottom' && styles.modalBackgroundBottom
                            ]}
                        >
                            <TouchableWithoutFeedback>
                                <Animated.View
                                    style={[
                                        styles.modalContainer,
                                        screen === 'full' && styles.modalContainerFull,
                                        screen === 'dropdown' && styles.modalContainerDropdown,
                                        offCanvas && DEVICE_WIDTH > 960
                                            ? styles.modalContainerOffCanvas
                                            : styles.modalContainerOffCanvassmall,
                                        {
                                            transform: offCanvas
                                                ? [{ translateX: translateValue }]
                                                : [{ scale: scaleValue }]
                                        },
                                        flatTop && styles.modalContainerTopFlat,
                                        flatBottom && styles.modalContainerBottomFlat
                                    ]}
                                >
                                    <View style={styles.header}>
                                        <Text style={{ fontWeight: 'bold' }}>{ }</Text>
                                        <TouchableOpacity
                                            style={{
                                                padding: 3.5,
                                                height: 30,
                                                width: 55,
                                                alignItems: 'center',
                                                backgroundColor: '#0088CF',

                                                borderWidth: 0,
                                                borderRadius: 10
                                            }}
                                            onPress={onClose}
                                        >
                                            <Text style={{ color: 'white', fontWeight: 'bold' }}>{t('Close')}</Text>
                                        </TouchableOpacity>
                                    </View>
                                    <View style={styles.body}>
                                        <Text style={{ fontWeight: 'bold', marginBottom: 5, fontSize: 17 }}>
                                            {headerText}
                                        </Text>

                                        <RenderHtml
                                            contentWidth={100}
                                            source={{ html: bodyText }}
                                            customHTMLElementModels={customHTMLElementModels}
                                        />

                                    </View>
                                </Animated.View>
                            </TouchableWithoutFeedback>
                        </View>
                    </TouchableWithoutFeedback>
                </KeyboardAvoidingView>
            </SafeAreaView>
        </Modal>
    )
}

const styles = StyleSheet.create({
    modalBackground: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center'
    },
    modalBackgroundTop: {
        justifyContent: 'flex-start'
    },
    modalBackgroundBottom: {
        justifyContent: 'flex-end'
    },
    modalContainer: {
        width: '90%',
        backgroundColor: 'white',
        borderRadius: 40,
        elevation: 20
    },
    modalContainerFull: {
        width: '100%'
    },
    modalContainerDropdown: {
        width: '70%',
        marginVertical: 80
    },
    modalContainerBottomFlat: {
        borderBottomLeftRadius: 50,
        borderBottomRightRadius: 0
    },
    modalContainerTopFlat: {
        borderTopLeftRadius: 50,
        borderTopRightRadius: 0
    },
    modalContainerOffCanvas: {
        width: '30%',
        position: 'absolute',
        right: 0,
        height: '100%',
        borderTopLeftRadius: 80,
        borderBottomLeftRadius: 80,
        backgroundColor: 'white',
        elevation: 20,
        borderRadius: 30
    },
    modalContainerOffCanvassmall: {
        width: '70%',
        position: 'absolute',
        right: 0,
        height: '100%',
        borderTopLeftRadius: 80,
        borderBottomLeftRadius: 80,
        backgroundColor: 'white',
        elevation: 20
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: 10,
        marginLeft: 20,
        paddingHorizontal: 20
    },
    body: {
        paddingHorizontal: 20,
        paddingVertical: 10
    }
})

export default ModalDrawer
const customHTMLElementModels = {
    p: HTMLElementModel.fromCustomModel({
        tagName: 'p',
        mixedUAStyles: {
            margin: 0,
            lineHeight: 20
        },
        contentModel: HTMLContentModel.block
    })
}